import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { FormattedMessage } from "react-intl";
import Select, { ActionMeta, ValueType } from "react-select";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import ContentWrapper from "../../components/content-wrapper";
import { FullWidthLoader } from "../../components/full-width-loader";
import { useProperty } from "../../components/property-provider";
import { SectionHeading } from "../../components/section-heading";
import { CurrencyEntity } from "./domain/currency.entity";
import { getAllEntities, updateEntity } from "./infrastructure/currency.api";
import { CurrencyTable } from "./infrastructure/CurrencyTable";
import { CurrencyManageMessages } from "./messages";

const messages = {
  ...CurrencyManageMessages,
};

type OptionType = { value: string; label: string };

function getLabelCurrencyOptions(currency: CurrencyEntity | undefined): string {
  if (!currency) {
    return "";
  }
  return `${currency.code} | ${currency.name}`;
}

const isOptionType = (option: any): option is OptionType => {
  return option && typeof option.value === "string";
};

function CurrencyManager() {
  const propertyInfo = useProperty();
  const [currencies, setCurrencies] = useState<CurrencyEntity[]>([]);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSuccess, setSuccess] = useState<boolean>(false);

  const currencyDefault = useMemo<CurrencyEntity>(
    () =>
      currencies.find(c => c.primary) || {
        id: "EUR",
        code: "EUR",
        name: "Euro Member Countries, Euro",
        visible: true,
        primary: true,
      },
    [currencies]
  );

  const currencyOptionDefault = useMemo(() => {
    return {
      value: currencyDefault.code,
      label: getLabelCurrencyOptions(currencyDefault),
    };
  }, [currencyDefault]);
  const currencyOptions = useMemo<OptionType[]>(
    () =>
      currencies.map(c => ({
        value: c.code,
        label: getLabelCurrencyOptions(c),
      })),
    [currencies]
  );

  const handleOnUpdate: () => Promise<void> = useCallback(async () => {
    setErrors([]);
    setSuccess(false);
    for (const currency of currencies) {
      if (currency.isChanged) {
        try {
          await updateEntity(currency);
        } catch (e) {
          errors.push(e instanceof Error ? e.message : "Unexpected error occurred");
        }
      }
      if (!errors?.length) {
        setSuccess(true);
      }
    }
  }, [currencies, errors]);

  const handleCurrenciesOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, entity: CurrencyEntity) => {
      setCurrencies((prevCurrencies: CurrencyEntity[]) =>
        prevCurrencies.map(currency =>
          currency.id === entity.id
            ? { ...currency, visible: event.target.checked, isChanged: true }
            : currency
        )
      );
    },
    [setCurrencies]
  );

  const handleDefaultCurrencyOnChange = useCallback(
    (option: ValueType<OptionType, false>, _: ActionMeta<OptionType>) => {
      if (!isOptionType(option)) return;

      setCurrencies((prevCurrencies: CurrencyEntity[]) =>
        prevCurrencies.map(currency => {
          if (currency.id === option.value) {
            return { ...currency, primary: true, visible: true, isChanged: true };
          } else if (currency.primary) {
            return { ...currency, primary: false };
          }
          return currency;
        })
      );
    },
    [setCurrencies]
  );

  useEffect(() => {
    const fetchCurrencies = async () => {
      const currencies = await getAllEntities();
      setCurrencies(currencies);
      setLoading(false);
    };
    try {
      fetchCurrencies();
    } catch (e) {
      setLoading(false);
      setErrors(e instanceof Error ? [e.message] : ["Unexpected error occurred"]);
    }
  }, []);

  if (isLoading) {
    return <FullWidthLoader />;
  }

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...messages.entityName} />
      </SectionHeading>
      <Card>
        <Card.Body>
          <Form.Group>
            <Form.Label htmlFor="occupationType">
              <FormattedMessage {...messages.defaultCurrency} />
            </Form.Label>
            <Select
              options={currencyOptions}
              defaultValue={currencyOptionDefault}
              onChange={handleDefaultCurrencyOnChange}
            />
          </Form.Group>
          <CurrencyTable rows={currencies} onChange={handleCurrenciesOnChange} />
          {isSuccess && (
            <Alert variant="success">
              <strong>
                <FormattedMessage {...messages.saveSuccess} />
              </strong>
            </Alert>
          )}
          {errors?.length
            ? errors.map((e, index) => (
                <Alert key={index} variant="danger">
                  <strong>{e}</strong>
                </Alert>
              ))
            : null}
          <Button variant="primary" type="button" onClick={handleOnUpdate}>
            <FormattedMessage {...messages.save} />
          </Button>
        </Card.Body>
      </Card>
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(CurrencyManager);
